import React, { useState, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { useFetchData } from 'services/queries';
import Box from '@mui/material/Box';
import Card from '@mui/material/Card';
import IconButton from '@mui/material/IconButton';
import Tooltip from '@mui/material/Tooltip';
import Chip from '@mui/material/Chip';
import Typography from '@mui/material/Typography';
import Stack from '@mui/material/Stack';
import {
  DataGridPremium,
  GridToolbarQuickFilter,
  GridToolbarExport,
  GridToolbarFilterButton,
  GridToolbarColumnsButton
} from '@mui/x-data-grid-premium';
import { MdVisibility, MdWarning } from 'react-icons/md';
import { formatCurrency } from 'utils/formatters';
import dayjs from 'dayjs';
import PaymentSummaryCell from './components/PaymentSummaryCell';
import { PaymentStatusChip } from './PaymentStatusChip';
import { ORDER_STATUS_COLORS, STATUS_CONFIG } from 'constants';
import { getContrastTextColor } from 'utils/colorUtils';
import { isBeforeToday, getDaysUntilDate } from 'utils/dateUtils';
import { PAYMENT_STATUS, PAYMENT_ENDPOINTS, PAYMENT_STATUS_LABELS } from './constants';

// Custom toolbar without create button
const CustomToolbar = () => {
  return (
    <Stack
      direction="row"
      justifyContent="space-between"
      alignItems="flex-start"
      spacing={2}
      py={1}
    >
      <Box color="secondary" display="flex" justifyContent="flex-end" alignItems="flex-start">
        <GridToolbarQuickFilter />
      </Box>
      <Stack
        direction="row"
        justifyContent="flex-end"
        alignItems="center"
        spacing={3}
      >
        <GridToolbarColumnsButton />
        <GridToolbarFilterButton />
        <GridToolbarExport />
      </Stack>
    </Stack>
  );
};

const PaymentOrdersOverview = ({ paymentEndpoint, title }) => {
  const navigate = useNavigate();
  const [columnVisibilityModel, setColumnVisibilityModel] = useState({});

  // Fetch all orders with payment information
  // Using report-sales-order for better performance (lighter payload)
  const { data: orders = [], isLoading: loadingOrders } = useFetchData('/order/report-sales-order/');
  const { data: payments = [], isLoading: loadingPayments } = useFetchData(
    paymentEndpoint || PAYMENT_ENDPOINTS.LIST,
    { staleTime: 5 * 60 * 1000 }
  );

  // Process orders to include payment summary
  const ordersWithPaymentInfo = useMemo(() => {
    if (!orders || !payments) return [];

    // Determine if this is a filtered endpoint
    // If endpoint is the base LIST (unfiltered), show ALL orders even without payments
    // If endpoint is filtered (status, overdue, etc.), only show orders with matching payments
    const isFilteredEndpoint = paymentEndpoint && paymentEndpoint !== PAYMENT_ENDPOINTS.LIST;

    const ordersToShow = isFilteredEndpoint
      ? orders.filter(order => payments.some(p => p.sales_order === order.id))
      : orders;

    return ordersToShow.map(order => {
      const orderPayments = payments.filter(p => p.sales_order === order.id);

      // Calculate total of all active payments (excluding CANCELLED) - this is our "order total"
      const totalPaymentsUsd = orderPayments
        .filter(p => p.status !== PAYMENT_STATUS.CANCELLED)
        .reduce((sum, p) => sum + parseFloat(p.usd_value || 0), 0);

      const totalPaidUsd = orderPayments
        .filter(p => p.status === PAYMENT_STATUS.PAID)
        .reduce((sum, p) => sum + parseFloat(p.usd_value || 0), 0);
      const totalPaidBrl = orderPayments
        .filter(p => p.status === PAYMENT_STATUS.PAID)
        .reduce((sum, p) => sum + parseFloat(p.brl_value || 0), 0);

      // Find the next unpaid payment by filtering out PAID and CANCELLED,
      // then sorting by payment_id ASC
      const unpaidPayments = orderPayments.filter(p =>
        p.status !== PAYMENT_STATUS.PAID &&
        p.status !== PAYMENT_STATUS.CANCELLED &&
        p.payment_id !== null
      );

      const nextPayment = unpaidPayments.length > 0
        ? unpaidPayments.sort((a, b) => a.payment_id - b.payment_id)[0]
        : null;

      // Calculate progress based on paid / total payments
      const paymentProgress = totalPaymentsUsd > 0 ? (totalPaidUsd / totalPaymentsUsd) * 100 : 0;

      let paymentStatus = 'No Payments';
      if (paymentProgress >= 100) {
        paymentStatus = 'Fully Paid';
      } else if (paymentProgress > 0) {
        paymentStatus = 'Partially Paid';
      } else if (orderPayments.length > 0) {
        paymentStatus = 'Pending';
      }

      // Check for overdue payments
      const hasOverdue = orderPayments.some(p => {
        if (p.status !== PAYMENT_STATUS.AWAITING_PAYMENT || !p.due_date) return false;
        return isBeforeToday(p.due_date);
      });

      // Extract client name for filtering
      // report-sales-order returns social_reason at root level
      const clientName = order.social_reason;

      return {
        ...order,
        client_name: clientName,  // Add client_name as a direct field for filtering
        payment_count: orderPayments.length,
        total_value: totalPaymentsUsd,  // Use payment totals as order total
        total_paid: totalPaidUsd,
        total_paid_brl: totalPaidBrl,
        payment_progress: paymentProgress,
        payment_status: paymentStatus,
        next_payment_date: nextPayment?.due_date,
        next_payment_amount: nextPayment?.usd_value,
        next_payment_id: nextPayment?.payment_id,
        next_payment_status: nextPayment?.status,
        next_payment_effective_status: nextPayment?.effective_status,
        next_payment_is_overdue: nextPayment?.is_overdue,
        next_payment_paid_late: nextPayment?.paid_late,
        next_payment_days_late: nextPayment?.days_late,
        has_overdue: hasOverdue
      };
    });
  }, [orders, payments, paymentEndpoint]);

  const columns = [
    {
      field: 'actions',
      headerName: '',
      width: 15,
      sortable: false,
      filterable: false,
      disableExport: true,
      cellClassName: 'v-center-cell',
      renderCell: (params) => {
        if (!params || !params.row) return null;
        return (
          <Box display="flex" gap={0.5}>
            <Tooltip title="View Payment Details">
              <IconButton
                size="small"
                onClick={() => navigate(`/admin/payment/order/${params.row.id}`)}
                sx={{ color: 'primary.main' }}
              >
                <MdVisibility size={18} />
              </IconButton>
            </Tooltip>
          </Box>
        );
      }
    },
    {
      field: 'order_identifier',
      headerName: 'Order ID',
      width: 100,
      cellClassName: 'v-center-cell',
      renderCell: (params) => {
        if (!params) return null;
        return (
          <Typography variant="body2" fontWeight="bold">
            {params.value || ''}
          </Typography>
        );
      }
    },
    {
      field: 'client_name',
      headerName: 'Client',
      minWidth: 150,
      flex: 1,
      cellClassName: 'v-center-cell',
      renderCell: (params) => {
        if (!params || !params.row) return null;
        // Since client_name is now a direct field in the data, we can use params.value
        return <Typography variant="body2">{params.value || ''}</Typography>;
      }
    },
    {
      field: 'payment_summary',
      headerName: 'Payment Summary',
      width: 200,
      sortable: false,
      filterable: false,
      groupable: false,
      aggregable: false,
      disableColumnMenu: true,
      disableExport: true,
      renderCell: (params) => {
        if (!params || !params.row) return null;
        return (
          <PaymentSummaryCell
            totalValue={params.row.total_value}
            totalPaid={params.row.total_paid}
            paymentCount={params.row.payment_count}
            hasOverdue={params.row.has_overdue}
            nextPaymentStatus={params.row.next_payment_status}
            nextPaymentEffectiveStatus={params.row.next_payment_effective_status}
          />
        );
      }
    },
    {
      field: 'next_payment_date',
      headerName: 'Next Payment',
      width: 150,
      type: 'date',
      valueGetter: (_, row) => {
        // Return Date object for sorting, or far-future date for nulls (sorts to end)
        // Use dayjs to parse date to avoid timezone conversion issues
        return row.next_payment_date ? dayjs(row.next_payment_date).toDate() : new Date(9999, 11, 31);
      },
      sortComparator: (v1, v2) => {
        const isFarFuture1 = v1.getFullYear() === 9999;
        const isFarFuture2 = v2.getFullYear() === 9999;

        // Both are far-future (no dates) - equal
        if (isFarFuture1 && isFarFuture2) return 0;
        // v1 is far-future (no date) - sort to end using large value
        if (isFarFuture1) return Number.MAX_SAFE_INTEGER;
        // v2 is far-future (no date) - sort to end using large negative value
        if (isFarFuture2) return -Number.MAX_SAFE_INTEGER;
        // Both are real dates - sort normally
        return v1.getTime() - v2.getTime();
      },
      renderCell: (params) => {
        if (!params || !params.row) return '-';
        const { next_payment_id, next_payment_date, next_payment_amount } = params.row;

        // If no payment ID, return dash
        if (!next_payment_id) return '-';

        // Case 1: Payment has a due date
        if (next_payment_date) {
          // Use dayjs to parse date to avoid timezone conversion issues (31/08 vs 01/09)
          const date = dayjs(next_payment_date).toDate();
          const isOverdue = date < new Date();

          return (
            <Box sx={{ display: 'flex', flexDirection: 'column', justifyContent: 'center', height: '100%' }}>
              <Typography
                variant="body2"
                color={isOverdue ? 'error' : 'text.primary'}
                fontWeight={isOverdue ? 'bold' : 'normal'}
              >
                {dayjs(date).format('DD/MM/YYYY')}
              </Typography>
              <Typography variant="caption" display="block" color="text.secondary">
                {formatCurrency(next_payment_amount, 'USD')}
              </Typography>
            </Box>
          );
        }

        // Case 2: Payment exists but no due date - show warning
        return (
          <Box sx={{ display: 'flex', flexDirection: 'column', justifyContent: 'center', height: '100%' }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mb: 0.25 }}>
              <Typography variant="body2" color="text.primary">
                Payment #{next_payment_id}
              </Typography>
              <Tooltip title="This payment does not have a due date set" arrow placement="top">
                <Box sx={{ display: 'flex', alignItems: 'center' }}>
                  <MdWarning size={16} style={{ color: '#ed6c02' }} />
                </Box>
              </Tooltip>
            </Box>
            <Typography variant="caption" display="block" color="text.secondary">
              {formatCurrency(next_payment_amount, 'USD')}
            </Typography>
            <Typography variant="caption" display="block" color="warning.main" sx={{ fontStyle: 'italic', fontSize: '0.7rem' }}>
              Due date pending
            </Typography>
          </Box>
        );
      }
    },
    {
      field: 'next_payment_status',
      headerName: 'N. Payment Status',
      width: 180,
      cellClassName: 'v-center-cell',
      type: 'singleSelect',
      valueOptions: Object.keys(PAYMENT_STATUS_LABELS),
      valueGetter: (_, row) => {
        // Return the effective_status or status for filtering/export
        return row.next_payment_effective_status || row.next_payment_status || '';
      },
      renderCell: (params) => {
        if (!params || !params.row) return '-';
        const {
          next_payment_status,
          next_payment_effective_status,
          next_payment_is_overdue,
          next_payment_paid_late,
          next_payment_days_late
        } = params.row;

        // If no next payment status, return dash
        if (!next_payment_status) return '-';

        return (
          <PaymentStatusChip
            status={next_payment_status}
            effective_status={next_payment_effective_status}
            is_overdue={next_payment_is_overdue}
            paid_late={next_payment_paid_late}
            days_late={next_payment_days_late}
          />
        );
      }
    },
    {
      field: 'order_status',
      headerName: 'Order Status',
      width: 140,
      type: 'singleSelect',
      valueOptions: Object.keys(STATUS_CONFIG || {}),
      valueGetter: (_, row) => {
        // Return status string for filtering/export
        return typeof row.status === 'object'
          ? row.status?.status || row.status?.name || ''
          : row.status || '';
      },
      renderCell: (params) => {
        if (!params || !params.row) return null;
        // report-sales-order returns status as a string directly
        const status = typeof params.row.status === 'object'
          ? params.row.status?.status || params.row.status?.name
          : params.row.status;
        const bg = ORDER_STATUS_COLORS?.[status] || '#BDC3C7';
        const fg = getContrastTextColor(bg);
        return (
          <Chip
            label={status || 'Unknown'}
            size="small"
            variant="filled"
            sx={{
              fontWeight: 500,
              minWidth: 110,
              backgroundColor: bg,
              color: fg
            }}
          />
        );
      }
    },
  ];

  const getRowClassName = (params) => {
    if (!params || !params.row) return '';

    // Priority 1: Overdue (red) - highest priority
    if (params.row.next_payment_effective_status === 'OVERDUE') {
      return 'overdue-row';
    }

    // Priority 2: Fully Paid (green)
    if (params.row.payment_status === 'Fully Paid') {
      return 'paid-row';
    }

    // Priority 3 & 4: Check days until due date for unpaid payments
    const nextPaymentDate = params.row.next_payment_date;
    const nextPaymentStatus = params.row.next_payment_status;

    // Only apply date-based coloring for unpaid payments
    if (nextPaymentDate && nextPaymentStatus && nextPaymentStatus !== PAYMENT_STATUS.PAID) {
      const daysUntil = getDaysUntilDate(nextPaymentDate);

      if (daysUntil !== null && daysUntil >= 0) {
        // Orange: due in next 10 days (0-9 days)
        if (daysUntil < 10) {
          return 'urgent-row';
        }
        // Yellow: due in 10-20 days (10-19 days)
        if (daysUntil >= 10 && daysUntil < 20) {
          return 'warning-row';
        }
      }
    }

    return '';
  };

  if (loadingOrders || loadingPayments) return <div>Loading...</div>;

  return (
    <Card extra={'w-full h-full bg-white p-4 shadow-lg'}>
      {title && (
        <Typography variant="h6" gutterBottom sx={{ mb: 2 }}>
          {title}
        </Typography>
      )}
      <Box p={1} sx={{
        height: 700,
        width: '100%',
        '& .MuiDataGrid-root': {
          border: 'none',
          '& .MuiDataGrid-cell': {
            borderBottom: '1px solid #F3F4F6',
          },
          '& .v-center-cell': {
            display: 'flex',
            alignItems: 'center'
          },
          '& .MuiDataGrid-columnHeaders': {
            backgroundColor: '#F9FAFB',
            borderBottom: '2px solid #E5E7EB',
          },
          '& .MuiDataGrid-row:hover': {
            backgroundColor: '#F9FAFB',
            cursor: 'pointer',
          },
          '& .overdue-row': {
            backgroundColor: '#FFF5F5', // Light red for overdue
            '&:hover': {
              backgroundColor: '#FFE5E5 !important',
            }
          },
          '& .urgent-row': {
            backgroundColor: '#FFF4ED', // Light orange for due in next 10 days
            '&:hover': {
              backgroundColor: '#FFE7D6 !important',
            }
          },
          '& .warning-row': {
            backgroundColor: '#FFFBEB', // Light yellow for due in 10-20 days
            '&:hover': {
              backgroundColor: '#FEF3C7 !important',
            }
          },
          '& .paid-row': {
            backgroundColor: '#F0FDF4', // Light green for paid
            '&:hover': {
              backgroundColor: '#E5F7ED !important',
            }
          }
        }
      }}>
        <DataGridPremium
          rows={ordersWithPaymentInfo}
          columns={columns}
          pagination
          pageSizeOptions={[25, 50, 100]}
          initialState={{
            density: 'comfortable',
            pagination: { paginationModel: { pageSize: 25 } },
            sorting: {
              sortModel: [{ field: 'next_payment_date', sort: 'asc' }]
            },
          }}
          columnVisibilityModel={columnVisibilityModel}
          onColumnVisibilityModelChange={setColumnVisibilityModel}
          getRowClassName={getRowClassName}
          onRowClick={(params) => {
            if (!params || !params.row) return;
            navigate(`/admin/payment/order/${params.row.id}`);
          }}
          slots={{
            toolbar: CustomToolbar
          }}
        />
      </Box>
    </Card>
  );
};

export default PaymentOrdersOverview;